import React, { useContext, useState, useEffect } from 'react';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CollectionsIcon from '@mui/icons-material/Collections';
import LinkIcon from '@mui/icons-material/Link';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ImageIcon from '@mui/icons-material/Image';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { I18nReactContext } from '../../../../../../contexts/I18nContext';
import { getCompanyImageBankAPI, getCompanyVideoBankAPI, uploadImageComponentAPI, uploadVideoComponentAPI } from '../../../../../../Services/api';
import { SA_uploadImageComponentAPI, SA_uploadVideoComponentAPI, getPublicAssetsAPI } from '../../../../../../Services/PublicAssets_api';

// Media type constants
const MEDIA_TYPE = {
    IMAGE: 'IMAGE',
    VIDEO: 'VIDEO'
};

/**
 * Media source section for image/video components
 * Handles upload, URL input, and media bank selection
 * For background components, also includes the image/video type selector tabs
 * @param {object} props - Component props
 * @param {object} props.component - The component being edited
 * @param {function} props.onContentChange - Callback when content changes
 * @param {string} props.signDocId - SignDoc ID for uploads
 * @param {string} props.sectionId - Section ID for uploads
 * @param {boolean} props.isTemplateMode - Whether in template editing mode
 * @param {string} props.templateId - Template ID for template mode uploads
 * @param {string} props.effectiveComponentType - Effective component type for i18n keys
 * @param {string} props.effectiveType - Effective media type ('image' or 'video')
 * @param {string} props.backgroundType - Current background type for background components
 * @param {function} props.onBackgroundTypeChange - Callback when background type changes (for background components)
 */
export const MediaSourceSection = ({
    component,
    onContentChange,
    signDocId,
    sectionId,
    isTemplateMode = false,
    templateId,
    effectiveComponentType,
    effectiveType,
    backgroundType,
    onBackgroundTypeChange
}) => {
    const { i18n } = useContext(I18nReactContext);
    const [mediaBank, setMediaBank] = useState([]);
    const [allMediaItems, setAllMediaItems] = useState([]); // Store all items for company mode pagination
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showUrlInput, setShowUrlInput] = useState(false);
    const [mediaUrl, setMediaUrl] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 5;

    /**
     * Loads media bank (images or videos) with pagination support
     * For template mode: uses server-side pagination
     * For company mode: loads all items once and paginates locally
     * @param {string} type - Media type: MEDIA_TYPE.IMAGE or MEDIA_TYPE.VIDEO
     * @param {number} page - Page number to load (default: currentPage)
     * @param {boolean} forceReload - Force reload all items (for company mode after upload)
     * @returns {Promise<Array>} Array of media items for current page
     */
    const loadMediaBank = async (type, page = currentPage, forceReload = false) => {
        setIsLoading(true);
        try {
            let mediaItems;
            let count = 0;

            if (isTemplateMode) {
                // Template mode - server-side pagination
                const response = await getPublicAssetsAPI({
                    paginationModel: { page, pageSize },
                    filterModel: { filterValues: { type } },
                    sortModel: { field: 'createdAt', sort: 'desc' }
                });
                if (response && response.data) {
                    mediaItems = response.data.map(asset => ({
                        key: asset.id,
                        fileName: asset.fileName,
                        signedUrl: asset.signedUrl,
                    }));
                    count = response.count || 0;
                } else {
                    mediaItems = [];
                }
                setTotalCount(count);
                setCurrentPage(page);
                setMediaBank(mediaItems || []);
            } else {
                // Company mode - load all items once, then paginate locally
                let allItems = allMediaItems;

                // Only fetch from API if we don't have items cached or force reload
                if (allItems.length === 0 || forceReload) {
                    allItems = type === MEDIA_TYPE.IMAGE
                        ? await getCompanyImageBankAPI()
                        : await getCompanyVideoBankAPI();
                    allItems = allItems || [];
                    setAllMediaItems(allItems);
                }

                count = allItems.length;
                setTotalCount(count);
                setCurrentPage(page);

                // Get items for current page (local pagination)
                const startIndex = page * pageSize;
                const endIndex = startIndex + pageSize;
                mediaItems = allItems.slice(startIndex, endIndex);
                setMediaBank(mediaItems);
            }

            return mediaItems || [];
        } catch (error) {
            console.error(`Error loading ${type.toLowerCase()} bank:`, error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    // Load media bank when component type changes or background type changes
    useEffect(() => {
        if (!component) return;
        const componentType = component.type;
        const isBgComponent = componentType === 'backgroundImage' || componentType === 'backgroundVideo';

        if (componentType === 'image' || componentType === 'video' || componentType === 'backgroundImage' || componentType === 'backgroundVideo') {
            // Reset cached items when type changes to force reload
            setAllMediaItems([]);

            const loadMedia = async () => {
                try {
                    if (isBgComponent) {
                        const mediaType = backgroundType === 'image' ? MEDIA_TYPE.IMAGE : MEDIA_TYPE.VIDEO;
                        await loadMediaBank(mediaType, 0, true);
                    } else {
                        const mediaType = componentType === 'image' ? MEDIA_TYPE.IMAGE : MEDIA_TYPE.VIDEO;
                        await loadMediaBank(mediaType, 0, true);
                    }
                } catch (error) {
                    console.error('Failed to load media bank:', error);
                }
            };
            loadMedia();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [component?.type, backgroundType, isTemplateMode]);

    const handleSelectMedia = (media) => {
        if (effectiveComponentType === 'backgroundImage') {
            onContentChange('background-image', media.key, { signedUrl: media.signedUrl });
        } else if (effectiveComponentType === 'backgroundVideo') {
            onContentChange('background-video', media.key, { signedUrl: media.signedUrl });
        } else {
            onContentChange(component.id, media.key);
        }
        setShowUrlInput(false);
    };

    const handleNextPage = () => {
        const newPage = currentPage + 1;
        const mediaType = effectiveType === 'image' ? MEDIA_TYPE.IMAGE : MEDIA_TYPE.VIDEO;
        loadMediaBank(mediaType, newPage);
    };

    const handlePreviousPage = () => {
        const newPage = Math.max(0, currentPage - 1);
        const mediaType = effectiveType === 'image' ? MEDIA_TYPE.IMAGE : MEDIA_TYPE.VIDEO;
        loadMediaBank(mediaType, newPage);
    };

    const handleFileChange = async (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const tempPreviewUrl = URL.createObjectURL(file);

            if (component.type === 'backgroundImage' || component.type === 'backgroundVideo') {
                onContentChange(component.id, tempPreviewUrl, { signedUrl: tempPreviewUrl });
            }
            // For regular image/video components, the preview will update when onContentChange is called after upload

            setIsUploading(true);

            try {
                let response;
                if (isTemplateMode && templateId) {
                    const uploadAPI = effectiveComponentType === 'image' || effectiveComponentType === 'backgroundImage'
                        ? SA_uploadImageComponentAPI
                        : SA_uploadVideoComponentAPI;
                    const componentIdToSend = (effectiveComponentType === 'backgroundImage' || effectiveComponentType === 'backgroundVideo')
                        ? null
                        : component.id;

                    response = await uploadAPI({
                        files: e.target.files,
                        templateId,
                        sectionId,
                        componentId: componentIdToSend
                    });
                } else {
                    const uploadAPI = effectiveComponentType === 'image' || effectiveComponentType === 'backgroundImage'
                        ? uploadImageComponentAPI
                        : uploadVideoComponentAPI;
                    const componentIdToSend = (effectiveComponentType === 'backgroundImage' || effectiveComponentType === 'backgroundVideo')
                        ? null
                        : component.id;

                    response = await uploadAPI({
                        files: e.target.files,
                        signDocId,
                        sectionId,
                        componentId: componentIdToSend
                    });
                }

                // Handle different response formats
                const result = response?.uploadResult || response;
                const uploadedFile = Array.isArray(result) ? result[0] : result;

                if (uploadedFile && (uploadedFile.signedUrl || uploadedFile.key)) {
                    const fileKey = uploadedFile.key || uploadedFile.id;
                    const fileUrl = uploadedFile.signedUrl;

                    if (component.type === 'backgroundImage') {
                        onContentChange(component.id, fileKey, { signedUrl: fileUrl });
                    } else if (component.type === 'backgroundVideo') {
                        onContentChange(component.id, fileKey, { signedUrl: fileUrl });
                    } else {
                        onContentChange(component.id, fileKey);
                    }

                    // Refresh media bank - reset to page 0 to show newly uploaded file
                    // Force reload to get the new file from the server
                    try {
                        setAllMediaItems([]); // Clear cache to force reload
                        const mediaType = (component.type === 'image' || component.type === 'backgroundImage')
                            ? MEDIA_TYPE.IMAGE
                            : MEDIA_TYPE.VIDEO;
                        await loadMediaBank(mediaType, 0, true);
                    } catch (refreshError) {
                        console.error('Error refreshing media bank:', refreshError);
                    }
                }

                setShowUrlInput(false);
            } catch (error) {
                console.error('Error uploading file:', error);
                setShowUrlInput(false);
            } finally {
                setIsUploading(false);
            }
        }
    };

    const handleUrlSubmit = () => {
        if (mediaUrl) {
            onContentChange(component.id, mediaUrl);
            setShowUrlInput(false);
            setMediaUrl('');
        }
    };

    const isImageType = effectiveComponentType === 'image' || effectiveComponentType === 'backgroundImage';
    const isBackgroundComponent = component?.type === 'backgroundImage' || component?.type === 'backgroundVideo';

    return (
        <div className="menu-section">
            {/* Background type selector tabs - only shown for background components */}
            {isBackgroundComponent && onBackgroundTypeChange && (
                <div className="background-type-tabs">
                    <button
                        className={`tab-button ${backgroundType === 'image' ? 'active' : ''}`}
                        onClick={() => onBackgroundTypeChange('image')}
                    >
                        <ImageIcon fontSize="small" />
                        <span>{i18n.translate('weezbeeCourse.editor.backgroundSelector.image', { defaultValue: 'Image' })}</span>
                    </button>
                    <button
                        className={`tab-button ${backgroundType === 'video' ? 'active' : ''}`}
                        onClick={() => onBackgroundTypeChange('video')}
                    >
                        <VideoLibraryIcon fontSize="small" />
                        <span>{i18n.translate('weezbeeCourse.editor.backgroundSelector.video', { defaultValue: 'Video' })}</span>
                    </button>
                </div>
            )}

            {showUrlInput ? (
                <div className="url-input-section">
                    <h4>
                        <LinkIcon />
                        <span>{i18n.translate(`weezbeeCourse.editor.${effectiveComponentType}Component.enterUrl`)}</span>
                    </h4>
                    <input
                        type="text"
                        value={mediaUrl}
                        onChange={(e) => setMediaUrl(e.target.value)}
                        placeholder={i18n.translate(`weezbeeCourse.editor.${effectiveComponentType}Component.urlPlaceholder`)}
                    />
                    <div className="url-input-actions">
                        <button onClick={handleUrlSubmit}>
                            {i18n.translate(`weezbeeCourse.editor.${effectiveComponentType}Component.submit`)}
                        </button>
                        <button onClick={() => setShowUrlInput(false)}>
                            {i18n.translate(`weezbeeCourse.editor.${effectiveComponentType}Component.cancel`)}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="media-selector-options">
                    <div className="media-selector-option">
                        {isUploading ? (
                            <div className="loading-indicator">
                                {i18n.translate(`weezbeeCourse.editor.${effectiveComponentType}Component.loading`)}
                            </div>
                        ) : (
                            <>
                                <label htmlFor="file-upload" className="custom-file-upload">
                                    <FileUploadIcon />
                                    <span>{i18n.translate(`weezbeeCourse.editor.${effectiveComponentType}Component.uploadNew`, { defaultValue: 'Upload New' })}</span>
                                </label>
                                <input
                                    id="file-upload"
                                    type="file"
                                    accept={isImageType ? "image/*" : "video/*"}
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                />
                            </>
                        )}
                    </div>

                    <div className="media-selector-option">
                        <button className="url-button" onClick={() => setShowUrlInput(true)}>
                            <LinkIcon />
                            <span>{i18n.translate(`weezbeeCourse.editor.${effectiveComponentType}Component.enterUrl`, { defaultValue: 'Enter URL' })}</span>
                        </button>
                    </div>

                    <div className="media-bank-section">
                        <h4>
                            <CollectionsIcon />
                            <span>{i18n.translate(`weezbeeCourse.editor.${effectiveComponentType}Component.${effectiveType}Bank`)}</span>
                        </h4>

                        {isLoading ? (
                            <div className="loading-indicator">
                                {i18n.translate(`weezbeeCourse.editor.${effectiveComponentType}Component.loading`)}
                            </div>
                        ) : mediaBank.length > 0 ? (
                            <div className="media-bank-grid">
                                {mediaBank.filter(media => {
                                    const fileName = media.fileName.toLowerCase();
                                    if (isImageType) {
                                        return fileName.endsWith('.jpg') ||
                                               fileName.endsWith('.jpeg') ||
                                               fileName.endsWith('.png') ||
                                               fileName.endsWith('.gif') ||
                                               fileName.endsWith('.bmp') ||
                                               fileName.endsWith('.webp');
                                    } else {
                                        return fileName.endsWith('.mp4') ||
                                               fileName.endsWith('.webm') ||
                                               fileName.endsWith('.ogg') ||
                                               fileName.endsWith('.mov') ||
                                               fileName.endsWith('.avi') ||
                                               fileName.endsWith('.mkv');
                                    }
                                }).map((media) => (
                                    <div
                                        key={media.key}
                                        className="media-bank-item"
                                        onClick={() => handleSelectMedia(media)}
                                    >
                                        {isImageType ? (
                                            <img src={media.signedUrl} alt="" />
                                        ) : (
                                            <>
                                                <video src={media.signedUrl} />
                                                <div className="video-overlay">
                                                    <VideoLibraryIcon />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-bank">
                                {i18n.translate(`weezbeeCourse.editor.${effectiveComponentType}Component.emptyBank`)}
                            </div>
                        )}

                        {/* Pagination controls - shown for both template mode and company mode when there are more items than pageSize */}
                        {totalCount > pageSize && (
                            <div className="media-bank-pagination">
                                <button
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 0}
                                    className="pagination-button"
                                    data-testid="pagination-previous"
                                >
                                <ChevronRightIcon />
                                </button>
                                <span className="pagination-info">
                                    {i18n.translate('weezbeeCourse.editor.pagination.pageInfo', {
                                        context: {
                                            current: currentPage + 1,
                                            total: Math.ceil(totalCount / pageSize)
                                        }
                                    })}
                                </span>
                                <button
                                    onClick={handleNextPage}
                                    disabled={(currentPage + 1) * pageSize >= totalCount}
                                    className="pagination-button"
                                    data-testid="pagination-next"
                                >
                                    <ChevronLeftIcon />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
